/**
 * Issue #623 — recovery from "the lazy chunk vanished after a deploy".
 *
 * Both apps lazy-load nearly every route (`loadComponent: () => import(...)`)
 * and build with `outputHashing: all`. After a deploy, a browser that still
 * holds the OLD index.html asks for a `chunk-XXXX.js` that no longer exists;
 * nginx's `try_files $uri $uri/ /index.html` answers 200 with HTML, so the
 * dynamic import fails:
 *
 *   Safari    TypeError: Importing a module script failed.
 *   Chromium  TypeError: Failed to fetch dynamically imported module: .../chunk-QK3BQ7XA.js
 *   MIME      'text/html' is not a valid JavaScript MIME type.
 *   Firefox   error loading dynamically imported module: .../chunk-QK3BQ7XA.js
 *
 * The navigation dies and the page looks frozen — with no way out. ~300 Sentry
 * events, 63% of them at the event gate (/checkin/:id/).
 *
 * The recovery is deliberately small: ONE automatic reload (which re-fetches
 * index.html and therefore the new chunk names), a guard so that reload can
 * never become a loop, and — if the reload did not help — a static "reload me"
 * overlay that depends on no lazy chunk at all.
 *
 * HOW IT IS WIRED, and why it is not an ErrorHandler:
 * `provideRouter(routes, withNavigationErrorHandler(handleChunkLoadNavigationError))`.
 * Providing `ErrorHandler` would REPLACE Sentry's handler and silence error
 * reporting in both apps. The router feature is purely additive.
 *
 * THE ROUTER'S CONTRACT (Angular 21, `_router-chunk.mjs`): the handler runs
 * inside the app injection context; a returned `RedirectCommand` turns the
 * error into a redirect, and every other return value is IGNORED — the router
 * then emits `NavigationError` and rethrows the original error, exactly as it
 * does today. We always return `undefined`, so reporting is untouched and we
 * only add the recovery.
 *
 * That contract is also why nothing here may THROW: the router wraps the call
 * in its own try/catch, and an exception of ours would take the place of the
 * original error AND suppress the `NavigationError` event — Sentry would see
 * our bug instead of the real one. Every branch below is defensive.
 *
 * SCOPE, so nobody expects more than this can give:
 *  - `urlUpdateStrategy` is 'deferred' (the default in both apps), so on a
 *    click-through failure the browser URL is still the PREVIOUS page and the
 *    reload lands there rather than on the route that failed. For the deep-link
 *    case — the 63% of #623 events, someone opening /checkin/:id/ straight from
 *    a link — the URL is already the right one and the recovery is exact.
 *  - A bootstrap-level failure (`main-*.js` itself 404s) is out of scope by
 *    construction: no Angular code runs at all, so no handler of ours exists.
 *    That one can only be fixed on the server (keep old chunks around, or serve
 *    404 instead of index.html for *.js) — a separate ops change.
 */

/** sessionStorage key holding the timestamp of the reload we triggered. */
export const CHUNK_RELOAD_MARKER_KEY = 'chunk_reload_at';

/**
 * How long a marker blocks a further reload. Long enough that the reload which
 * fixes a fresh deploy can never re-trigger; short enough that the NEXT deploy
 * (hours or weeks later) still gets its one automatic recovery.
 */
export const CHUNK_RELOAD_TTL_MS = 60_000;

/** DOM id of the static fallback overlay (also its idempotency latch). */
export const CHUNK_RELOAD_OVERLAY_ID = 'chunk-reload-overlay';

/** Marker key merged into `history.state` — the storage-free guard layer. */
export const CHUNK_RELOAD_HISTORY_KEY = '__chunkReloadAt';

/** Depth of the wrapper walk (`error` / `rejection` / `cause` chains). */
const MAX_UNWRAP_DEPTH = 3;

/** Keys under which the original error hides inside a wrapper. */
const WRAPPER_KEYS = ['error', 'rejection', 'cause', 'originalError', 'reason'] as const;

/**
 * The chunk / dynamic-import family, and ONLY it. Deliberately narrow: a
 * reload on a generic failure is a boot loop for the user. In particular a
 * bare `Failed to fetch` (offline) is NOT here — only the form that names a
 * dynamically imported module.
 *
 * On the MIME entry: Chrome's "Strict MIME type checking is enforced" wording
 * is a CONSOLE message and never reaches JS — Chrome rejects the import with
 * the "Failed to fetch dynamically imported module" form, already covered. The
 * "is not a valid JavaScript MIME type" text that DOES reach JS is WebKit's.
 * Both are needed; do not prune either as a duplicate.
 */
const CHUNK_MESSAGE_PATTERNS = [
  'importing a module script failed',
  'failed to fetch dynamically imported module',
  'is not a valid javascript mime type',
  'error loading dynamically imported module',
];

const defaultNow = () => Date.now();
const defaultReload = () => window.location.reload();

// Seams. Kept as module-level bindings (not captured) so a test override is
// seen by handlers registered before it — including the overlay button.
let nowFn: () => number = defaultNow;
let reloadFn: () => void = defaultReload;

/**
 * Third guard layer, needed because the other two can BOTH be unwritable.
 * In an opaque-origin iframe `sessionStorage` throws AND `history.replaceState`
 * throws, so no marker can be persisted at all; without this latch every
 * navigation error in the page load would schedule another reload. It also
 * collapses two navigation errors that race within one page load into a single
 * reload. Scope is exactly one page load — which is all we need, since a reload
 * is precisely what ends it.
 */
let reloadScheduled = false;

function matchesChunkMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return CHUNK_MESSAGE_PATTERNS.some((pattern) => lower.includes(pattern));
}

function walk(err: unknown, depth: number, seen: Set<object>): boolean {
  if (err === null || err === undefined || depth < 0) {
    return false;
  }
  if (typeof err === 'string') {
    return matchesChunkMessage(err);
  }
  if (typeof err !== 'object') {
    return false;
  }
  // Wrapper chains can be cyclic (`e.error.cause === e`); a bounded depth plus
  // a visited set makes the walk terminate in every shape.
  if (seen.has(err as object)) {
    return false;
  }
  seen.add(err as object);

  const candidate = err as Record<string, unknown>;
  try {
    // The webpack-era name, cheap to keep: some builds/browsers still label it.
    if (typeof candidate['name'] === 'string' && candidate['name'] === 'ChunkLoadError') {
      return true;
    }
    const message = candidate['message'];
    if (typeof message === 'string' && matchesChunkMessage(message)) {
      return true;
    }
  } catch {
    // Exotic getters — fall through to the wrappers.
  }

  for (const key of WRAPPER_KEYS) {
    let nested: unknown;
    try {
      nested = candidate[key];
    } catch {
      continue;
    }
    if (walk(nested, depth - 1, seen)) {
      return true;
    }
  }
  return false;
}

/**
 * True only for the chunk / dynamic-import failure family, at any wrapping
 * depth: the bare error, a `NavigationError` (original under `.error`), a
 * rejection report (`.rejection`), or a re-throw that kept the original as
 * `.cause`.
 */
export function isChunkLoadError(err: unknown): boolean {
  return walk(err, MAX_UNWRAP_DEPTH, new Set<object>());
}

function readStorageMarker(): number | null {
  try {
    // Re-enter `window.sessionStorage` inside the try: on blocked storage the
    // PROPERTY ACCESS is what throws, not `getItem` (#622). This util keeps its
    // own guard on purpose — `safe-storage.ts` is localStorage-specific and its
    // sticky latch has different semantics.
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_MARKER_KEY);
    if (raw === null) {
      return null;
    }
    const timestamp = Number(raw);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
}

function readHistoryMarker(): number | null {
  try {
    const state = window.history.state;
    if (!state || typeof state !== 'object') {
      return null;
    }
    const timestamp = (state as Record<string, unknown>)[CHUNK_RELOAD_HISTORY_KEY];
    return typeof timestamp === 'number' && Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
}

/**
 * False as soon as EITHER layer shows a recent reload of ours. Two layers
 * because either one alone has a hole: sessionStorage throws for the #622
 * population, and `history.state` is lost on a hard navigation.
 */
export function shouldReload(): boolean {
  const now = nowFn();
  for (const timestamp of [readStorageMarker(), readHistoryMarker()]) {
    if (timestamp !== null && now - timestamp < CHUNK_RELOAD_TTL_MS) {
      return false;
    }
  }
  return true;
}

/**
 * Writes the marker in both layers, each defensively. Must run BEFORE the
 * reload: `history.state` survives `location.reload()` of the same entry, so
 * the marker is still there when the reloaded page hits the same error.
 */
export function markReloading(): void {
  const now = nowFn();
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_MARKER_KEY, String(now));
  } catch {
    // Blocked storage / quota — the history layer carries the guard.
  }
  try {
    const state = window.history.state;
    const base = state && typeof state === 'object' ? state : {};
    // Merge: the router keeps its own navigation state here.
    window.history.replaceState({ ...base, [CHUNK_RELOAD_HISTORY_KEY]: now }, '');
  } catch {
    // Nothing else to try; the storage layer carries the guard.
  }
}

/**
 * The end state when the reload did not help. Plain DOM with inline styles on
 * purpose: at this point we know a lazy chunk cannot be fetched, so anything
 * Angular-rendered (or any further import) could fail the same way.
 */
export function showReloadOverlay(): void {
  try {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }
    if (document.getElementById(CHUNK_RELOAD_OVERLAY_ID)) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.id = CHUNK_RELOAD_OVERLAY_ID;
    overlay.setAttribute('role', 'alertdialog');
    overlay.setAttribute('aria-label', 'Pagina nu s-a putut încărca');
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:2147483647',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'padding:16px',
      'background:rgba(0,0,0,0.55)',
      'font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif',
    ].join(';');

    const card = document.createElement('div');
    card.style.cssText = [
      'max-width:420px',
      'width:100%',
      'box-sizing:border-box',
      'padding:24px',
      'border-radius:12px',
      'background:#fff',
      'color:#212529',
      'text-align:center',
      'box-shadow:0 10px 40px rgba(0,0,0,0.3)',
    ].join(';');

    const message = document.createElement('p');
    message.style.cssText = 'margin:0 0 20px;font-size:16px;line-height:1.5';
    // Neutral on purpose: the same failure shape also comes from a flaky
    // network, where blaming a new deploy would simply be wrong.
    message.textContent = 'Pagina nu s-a putut încărca complet. Reîncarcă pentru a continua.';

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Reîncarcă pagina';
    button.style.cssText = [
      'display:inline-block',
      'padding:12px 24px',
      'border:0',
      'border-radius:8px',
      'background:#7b1f2b',
      'color:#fff',
      'font-size:16px',
      'cursor:pointer',
    ].join(';');
    button.addEventListener('click', () => {
      try {
        reloadFn();
      } catch {
        // A failed reload leaves the overlay up — still the best end state.
      }
    });

    card.appendChild(message);
    card.appendChild(button);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    // The page behind us is dead; put the only usable control under the cursor
    // and under the screen reader straight away.
    try {
      button.focus();
    } catch {
      // Focus is a nicety, never a reason to fail.
    }
  } catch {
    // An overlay we could not render must never break the router.
  }
}

/**
 * Wired via `withNavigationErrorHandler` in both apps. Acts only on the chunk
 * family; every other navigation error passes through untouched (we return
 * `undefined`, which the router ignores — see the contract at the top).
 */
export function handleChunkLoadNavigationError(navError: unknown): void {
  try {
    if (!isChunkLoadError(navError)) {
      return;
    }
    if (!reloadScheduled && shouldReload()) {
      reloadScheduled = true;

      // Mark now, so the storage layer is written even if anything below is
      // cut short.
      markReloading();

      // ...and mark AGAIN, one microtask later, because of what the Router
      // does the instant we return. Emitting NavigationError leads to
      // restoreHistory() → resetUrlToCurrentUrlTree() →
      // location.replaceState(..., generateNgRouterState(...)), which REPLACES
      // history.state with a fresh object — our marker included. That whole
      // chain is synchronous, so a microtask runs strictly after it, while
      // still running before the queued reload (location.reload() only queues a
      // navigation; the current task finishes first). Re-stamping here is what
      // makes the history layer actually survive to the next page load — and
      // for the #622 population, where sessionStorage throws, it is the ONLY
      // guard that can. Without it: infinite reload loop.
      queueMicrotask(() => {
        try {
          markReloading();
          reloadFn();
        } catch {
          // Nothing left to try; the page stays as it is rather than crashing.
        }
      });
      return;
    }
    showReloadOverlay();
  } catch {
    // Never throw out of here: the router would report OUR error instead of the
    // real one and would skip emitting NavigationError entirely.
  }
}

/** TEST-ONLY. Overrides the time / reload seams. */
export function __setChunkReloadSeamsForTests(seams: {
  now?: () => number;
  reload?: () => void;
}): void {
  if (seams.now) {
    nowFn = seams.now;
  }
  if (seams.reload) {
    reloadFn = seams.reload;
  }
}

/** TEST-ONLY. Restores the production seams and clears the page-load latch. */
export function __resetChunkReloadForTests(): void {
  nowFn = defaultNow;
  reloadFn = defaultReload;
  reloadScheduled = false;
}
